import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactResizeDetector from "react-resize-detector";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons/faChevronUp";
import { useLocation } from "react-router-dom";
import { event as readMoreEvent } from "../GA/ProviderInfo/Readmore";
import { useCategoryBreadcrumb } from "../hooks";

import styles from "./ReadMore.module.scss";

export const ReadMoreWithAnchor: React.FC<{}> = ({ children }) => {
    const ref = useRef<HTMLDivElement>();

    const { pathname } = useLocation();
    const { breadcrumb } = useCategoryBreadcrumb();
    const [allowedHeight, setAllowedHeight] = useState<number>();

    const [showReadMore, setShowReadMore] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);

    const calculateAllowedHeight = useCallback(() => {
        const current = ref.current;
        if (!current) return;
        const anchor = current.querySelector(".readmore-anchor");
        if (!anchor) {
            // setAllowedHeight(undefined);
            return undefined;
        }

        // anchor is assumed to be a direct child within readmore content element
        const offsetTop = (anchor as HTMLDivElement)?.offsetTop;

        if (offsetTop == null) {
            // setAllowedHeight(undefined);
            return undefined;
        }

        // setAllowedHeight(offsetTop);
        return offsetTop;
    }, []);

    const calculateHeight = useCallback(() => {
        const current = ref.current;
        if (!current) return 0;

        const totalHeight = _.sum(
            Array.from(current.children).map(child => {
                const element = child as HTMLElement;
                if (element?.classList?.contains(styles.hider)) return 0;

                return element?.getBoundingClientRect()?.height ?? 0;
            })
        );

        return totalHeight;
    }, []);

    const decideReadMoreVisibility = useCallback(
        (allowedHeight: number) => {
            if (ref.current) {
                const height = calculateHeight();

                if (allowedHeight == null) {
                    setShowReadMore(false);
                } else if (height > allowedHeight) {
                    setShowReadMore(true);
                } else {
                    setShowReadMore(false);
                }
            }
        },
        [
            setShowReadMore,
            calculateHeight
            // allowedHeight
        ]
    );

    const toggle = useCallback(() => {
        setOpen(!open);
        readMoreEvent(pathname, !open, breadcrumb);
    }, [open, pathname, breadcrumb]);

    // re-cal allowed height and decide visibility of readmore
    const refresh = useCallback(() => {
        const allowedHeight = calculateAllowedHeight();
        decideReadMoreVisibility(allowedHeight);
        setAllowedHeight(allowedHeight);
    }, [calculateAllowedHeight, decideReadMoreVisibility]);

    const maxHeight = useMemo(() => {
        if (!showReadMore) return undefined;

        if (!open) return allowedHeight;

        const childrenHeight = calculateHeight();
        return childrenHeight;
    }, [showReadMore, calculateHeight, allowedHeight, open]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <>
            <div
                ref={ref}
                className={styles.readMore}
                style={{
                    maxHeight,
                    position: "relative",
                    overflow: "hidden"
                }}
            >
                {children}

                {/* {showReadMore === true && (
                    <div
                        className={`${styles.hider} ${styles.noShadow} hider`}
                        style={{
                            opacity: open ? 0 : 1
                        }}
                    ></div>
                )} */}
                <ReactResizeDetector handleWidth={true} onResize={refresh} targetRef={ref} />
            </div>
            {showReadMore === true && (
                <div className={styles.readMoreButton} onClick={toggle}>
                    <span>
                        Read {open === true ? "less" : "more"}
                        {open === true ? (
                            <FontAwesomeIcon icon={faChevronUp} className="ml-1" />
                        ) : (
                            <FontAwesomeIcon icon={faChevronDown} className="ml-1" />
                        )}
                    </span>
                </div>
            )}
        </>
    );
};
